import * as React from 'react';
import {
  Flex,
  Icon,
  Tooltip,
  TouchLink,
  TouchLinkGroup,
  useIsMobile,
} from '@amzn/storm-ui';
import { MessageBundle } from '@amzn/arb-tools';
import { Account, AdvertisingAccountType } from '@amzn/aac-chrome-internal-api';
import { IA2AdvertisingAccountItem } from './IA2AdvertisingAccountItem';
import { HMHAnchor } from '@amzn/help-me-here-client';
import {
  AccountTypeLabel,
  AccountTypeLabelContainer,
  IA2AccountDropdownList,
  IA2AccountListItem,
  PaginationButton,
} from './styles';
import { NoAccountsMessage } from './NoAccountsMessage';
import { IA2MobileAccountListItem } from './IA2MobileAccountListItem';
import GlobalAccountTooltipAlert from './GlobalAccountTooltipContent';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getCountryCodeByMarketplace } from 'src/commons/utils/advertising-config-store/ACSConfigClient';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { useComponentExpiry } from '@amzn/storm-ui-utils';

import GlobalAccountTooltipAlertMobile from './GlobalAccountTooltipContentMobile';
import { infoCircle } from '@amzn/storm-ui-icons';
import useIsFeatureEnabled from 'src/commons/hooks/useIsFeatureEnabled';
import { WeblabedFeatures } from 'src/commons/constants';

export const PAGE_SIZE = 30;

const MULTI_MARKETPLACE_ACCOUNT_TYPES = [
  AdvertisingAccountType.GLOBAL,
  AdvertisingAccountType.DSP_ENTITY,
];

const ACCOUNT_TYPE_LABEL_MAPPING = new Map([
  [AdvertisingAccountType.DSP_ADVERTISER, 'account-dropdown-type-dsp'],
  [AdvertisingAccountType.DSP_ENTITY, 'account-dropdown-type-dsp'],
  [AdvertisingAccountType.SPONSORED_ADS, 'account-dropdown-type-sponsored-ads'],
  [AdvertisingAccountType.MANAGER, 'account-dropdown-type-manager'],
  [AdvertisingAccountType.AMC, 'account-switcher-filter-amc-label'],
  [AdvertisingAccountType.GLOBAL, 'account-dropdown-type-single-global'],
]);

type AccountTypeSectionProps = {
  accounts: Account[];
  label: string;
  type: AdvertisingAccountType;
  selectedEntityId?: string;
  isLastAccountType?: boolean;
};

const getCountryLabel = ({
  accountType,
  account,
  bundle,
}: {
  accountType: AdvertisingAccountType;
  account: Account;
  bundle: MessageBundle;
}) => {
  if (
    accountType === AdvertisingAccountType.MANAGER ||
    accountType === AdvertisingAccountType.DSP_ENTITY
  ) {
    return '';
  }

  if (MULTI_MARKETPLACE_ACCOUNT_TYPES.includes(accountType)) {
    return bundle.getMessage('account-switcher-multiple-countries');
  }

  let marketplaceId = account.marketplaceId;
  if (
    accountType === AdvertisingAccountType.SPONSORED_ADS &&
    Array.isArray(account.nestedAccounts)
  ) {
    if (account.nestedAccounts.length > 1) {
      return bundle.getMessage('account-switcher-multiple-countries');
    }

    if (account.nestedAccounts.length === 1) {
      marketplaceId = account.nestedAccounts[0].marketplaceId;
    }
  }

  if (!marketplaceId) {
    return '';
  }

  const countryCode = getCountryCodeByMarketplace(marketplaceId);

  if (countryCode) {
    return bundle.getMessage(`account-switcher-marketplace-${countryCode}`);
  }

  // fallback to not rendering anything
  return '';
};

const IA2DesktopAccountDropdownList: React.FC<{
  accounts: Account[];
  accountType: AdvertisingAccountType;
  selectedEntityId?: string;
  listReference: React.RefObject<HTMLUListElement>;
}> = ({ accounts, selectedEntityId, accountType, listReference }) => {
  const bundle = useAccountDropdownBundle();

  return (
    <IA2AccountDropdownList ref={listReference}>
      {accounts.map((account: Account) => {
        const isSelected = selectedEntityId === account.entityId;

        return (
          <IA2AccountListItem key={account.url} isSelected={isSelected}>
            <IA2AdvertisingAccountItem
              isSelected={isSelected}
              advertisingAccount={account}
              key={account.entityId}
              retailerName={account.retailerName}
              countryLabel={getCountryLabel({
                accountType,
                account,
                bundle,
              })}
            />
          </IA2AccountListItem>
        );
      })}
    </IA2AccountDropdownList>
  );
};

const IA2MobileAccountDropdownList: React.FC<{
  accounts: Account[];
  accountType: AdvertisingAccountType;
}> = ({ accounts, accountType }) => {
  const bundle = useAccountDropdownBundle();
  return (
    <TouchLinkGroup>
      {accounts.map((account) => {
        return (
          <TouchLink
            key={account.url}
            href={account.url}
            data-ccx-e2e-id="aac-account-link"
            {...getTaktAttributes('aac-account-link', chromeComponents.HEADER, {
              url: account.url,
            })}
          >
            <IA2MobileAccountListItem
              key={account.url}
              accountLabel={account.label}
              countryLabel={getCountryLabel({
                accountType,
                account,
                bundle,
              })}
              retailerName={account.retailerName}
            />
          </TouchLink>
        );
      })}
    </TouchLinkGroup>
  );
};

export const IA2AccountDropdownTypeSection: React.FC<
  AccountTypeSectionProps
> = ({ accounts, label, type, selectedEntityId, isLastAccountType }) => {
  const isMobile = useIsMobile();
  const isExpired = useComponentExpiry('2025-09-30');
  const [selectedPage, setSelectedPage] = React.useState(1);
  const bundle = useAccountDropdownBundle();
  const pages = Math.ceil(accounts.length / PAGE_SIZE);
  const selectedEndIndex = selectedPage * PAGE_SIZE;
  const listReference = React.useRef<HTMLUListElement>(null);

  // Check if this is the Global account type
  const isGlobalAccountType = type === AdvertisingAccountType.GLOBAL;

  // reset selected page if number of accounts change
  React.useEffect(() => {
    setSelectedPage(1);
  }, [accounts.length]);

  const paginate = () => {
    const previousPage = selectedPage;
    setSelectedPage(selectedPage + 1);
    setTimeout(() => {
      const focusElement =
        listReference.current &&
        listReference.current.children[previousPage * PAGE_SIZE];
      if (focusElement && focusElement.firstChild) {
        (focusElement.firstChild as HTMLElement).focus();
      }
    });
  };

  return (
    <Flex
      direction="column"
      alignItems="normal"
      paddingBlockStart={isMobile ? 'xxsmall' : 'none'}
    >
      <AccountTypeLabelContainer>
        <AccountTypeLabel weight="bold" withMargin={!isMobile}>
          {label}{' '}
          {isGlobalAccountType &&
            useIsFeatureEnabled(WeblabedFeatures.HELP_ME_HERE_SGA_TOOLTIP) && (
              <Tooltip trigger={<Icon type={infoCircle} />}>
                {bundle.getMessage(
                  'account-switcher-global-account-tooltip-label',
                )}
                <HMHAnchor contentId="GEABRS583GD87CLZ-GV4VBGPACU64WYCF" />
              </Tooltip>
            )}
          {isGlobalAccountType &&
            !isExpired &&
            (isMobile ? (
              <GlobalAccountTooltipAlertMobile />
            ) : (
              <GlobalAccountTooltipAlert />
            ))}
        </AccountTypeLabel>
      </AccountTypeLabelContainer>
      {(!accounts || accounts.length == 0) && <NoAccountsMessage />}
      {accounts && accounts.length > 0 && !isMobile && (
        <IA2DesktopAccountDropdownList
          selectedEntityId={selectedEntityId}
          accountType={type}
          accounts={accounts.slice(0, selectedEndIndex)}
          listReference={listReference}
        />
      )}
      {accounts && accounts.length > 0 && isMobile && (
        <IA2MobileAccountDropdownList
          accounts={accounts.slice(0, selectedEndIndex)}
          accountType={type}
        />
      )}
      {pages > 1 && selectedPage !== pages && (
        <PaginationButton
          onClick={paginate}
          extraPaddingAtBottom={isLastAccountType}
        >
          {(() => {
            const labelKey = ACCOUNT_TYPE_LABEL_MAPPING.get(type);
            if (labelKey) {
              return bundle.formatMessage(
                'account-dropdown-account-type-pagination',
                {
                  pageSize: PAGE_SIZE.toString(),
                  accountType: bundle.getMessage(labelKey),
                },
              );
            }

            return bundle.formatMessage(
              'account-dropdown-invalid-account-type-pagination',
              {
                pageSize: PAGE_SIZE.toString(),
              },
            );
          })()}
        </PaginationButton>
      )}
    </Flex>
  );
};
